<template>
  <ModalBase show-modal :auto-h="true" @close-modal="close" class="gk-modal-recurrent-invoice">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:pretitle>
          {{ (isEdit) ? $gettext('EDIT'): $gettext('ADD') }}
        </template>
        <template v-slot:title>
          {{ $gettext('Schedule Invoice') }}
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <b-form v-if="!loading" ref="recurrent-task-form" @submit="onSubmit">

        <!-- task -->
        <b-form-group id="recurrent-input-group-name" label-cols-sm="3" label-align-sm="right" content-cols-sm :label="$gettext('Task name')" label-for="recurrent-input-name">
          <b-form-input
            id="recurrent-input-name"
            v-model="recurrent.name"
            required
          />
        </b-form-group>

        <!-- recipients -->
        <b-form-group id="recurrent-input-group-emails" label-cols-sm="3" label-align-sm="right" :label="$gettext('Recipients')" label-for="recurrent-input-emails">
          <b-form-input
            id="recurrent-input-emails"
            v-model="recurrent.emails"
            placeholder="jane@example.com, fred@example.com"
            required
          />
        </b-form-group>

        <!-- message -->
        <b-form-group id="recurrent-input-group-comments" label-cols-sm="3" label-align-sm="right" :label="$gettext('Email Message')" label-for="recurrent-input-comments">
          <b-form-textarea
            id="recurrent-input-group-comments"
            v-model="recurrent.comments"
            :placeholder="$gettext('Dear ....')"
            rows="3" max-rows="6"
          />
        </b-form-group>

        <!-- start date -->
        <b-form-group v-if="!isEdit" id="recurrent-input-group-start_date" label-cols-sm="3" label-align-sm="right" :label="$gettext('Start date')" label-for="recurrent-input-start_date">
          <b-form-datepicker 
            id="recurrent-input-start_date"
            v-model="recurrent.start_date"
            :locale="locale"
          />
        </b-form-group>

        <!-- frequency -->
        <b-form-group id="recurrent-input-group-interval" label-cols-sm="3" label-align-sm="right" :label="$gettext('Frequency')" label-for="recurrent-input-interval">
          <b-form-row>
            <b-col cols="12" lg="5">
              <b-form-select id="recurrent-input-interval" v-model="recurrent.interval" :options="interval" required />
            </b-col>
            <b-col cols="12" lg="7">
              <b-form-group id="recurrent-input-group-frequency" label-cols-sm="3" label-align-sm="center" :label="frequencyLabelText" label-for="recurrent-input-frequency" v-slot="{ ariaDescribedby }">

                <!-- weeks -->
                <b-form-select v-if="recurrent.interval === 'weekly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                  <b-form-select-option v-for="idx in 3" :key="'weekly-' + idx" :value="idx">
                    {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('weeks') : $gettext('week') }}
                  </b-form-select-option>
                </b-form-select>

                <!-- months -->
                <b-form-select v-if="recurrent.interval === 'monthly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                  <b-form-select-option v-for="idx in 12" :key="'monthly-' + idx" :value="idx">
                    {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('months') : $gettext('month') }}
                  </b-form-select-option>
                </b-form-select>

                <!-- weekdays -->
                <b-form-checkbox-group
                  v-if="recurrent.interval === 'daily'"
                  id="recurrent-input-frequency"
                  v-model="recurrent.days"
                  :options="daily_options"
                  :aria-describedby="ariaDescribedby"
                  name="frequency-daily"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-form-group>

        <!-- end -->
        <b-form-group id="recurrent-input-group-ends" label-cols-sm="3" label-align-sm="right" :label="$gettext('Ends')" label-for="recurrent-input-end-method">
          
          <!-- never -->
          <b-form-row class="mb-2">
            <b-col cols="12">
              <b-form-radio id="recurrent-input-end-method" v-model="recurrent.end_method" name="end-method" value="endless" class="pt-2">{{ $gettext('never') }}</b-form-radio>
            </b-col>
          </b-form-row>
          
          <!-- after -->
          <b-form-row class="mb-2">
            <b-col cols="3">
              <b-form-radio v-model="recurrent.end_method" name="end-method" value="iteration" class="pt-2">{{ $gettext('after') }}</b-form-radio>
            </b-col>
            <b-col cols="9">

              <b-form-row :class="{ 'opacity-25': recurrent.end_method !== 'iteration' }">
                <b-col cols="6">
                  <b-form-input
                    id="recurrent-input-iterations"
                    v-model="recurrent.iterations"
                    :disabled="recurrent.end_method !== 'iteration'"
                    min="1" value="1" step="1"
                    type="number"
                  />
                </b-col>
                <b-col cols="6">
                  <label for="recurrent-input-iterations">{{ $gettext('times') }}</label>
                </b-col>
              </b-form-row>
    
            </b-col>
          </b-form-row>

          <!-- on -->
          <b-form-row>
            <b-col cols="3">
              <b-form-radio v-model="recurrent.end_method" name="end-method" value="date" class="pt-2">{{ $gettext('on') }}</b-form-radio>
            </b-col>
            <b-col cols="9" :class="{ 'opacity-25': recurrent.end_method !== 'date' }">
              <b-form-datepicker 
                id="recurrent-input-end_date"
                :disabled="recurrent.end_method !== 'date'"
                v-model="recurrent.end_date"
                :locale="locale"
                :min="recurrent.start_date"
              />
            </b-col>
          </b-form-row>
        </b-form-group>

        <!-- summary / next delivery-->
        <b-form-group id="recurrent-input-group-summary" label-cols-sm="3" label-align-sm="right" content-cols-sm :label="summaryLabelText" label-for="recurrent-input-name">
          <p v-if="isEdit" class="text-brand">
            {{ nextDeliveryDate }}
            <br>
            {{ recurrencySummary }}
          </p>
          <p class="text-blue" v-else>
            {{ createSummary }}
          </p>
        </b-form-group>

      </b-form>
    </template>
    <template v-slot:actions>
      <div class="d-flex w-100 justify-content-between">
        <div>
          <GekkoButton
            :text="$gettext('Cancel')"
            variant="light"
            @click="close()"
          />
        </div>
        <div class="d-flex" style="gap: 0.5rem;">
          <GekkoButton
            v-if="isEdit"
            :text="$gettext('View invoice')"
            @click="openViewInvoice"
            variant="outline-brand"
          />
          <GekkoButton
            v-if="isEdit"
            :text="$gettext('Update')"
            @click="prepareAndUpdate()"
          />
          <GekkoButton
            v-if="!isEdit"
            :text="$gettext('Save')"
            @click="prepareAndSave()"
          />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import AddProjectSimple from "@/components/contacts/AddProjectSimple"
import CustomerSelect from "@/components/invoices/ui/CustomerSelect"
import moment from 'moment'

export default {
  name: 'RecurrentInvoiceModal',
  components: { ModalBase, TitleBar, GekkoButton, AddProjectSimple, CustomerSelect },
  data () {
    return {
      loading: true,
      recurrent: {},
      interval: [
        { text: this.$gettext('Daily'), value: 'daily' },
        { text: this.$gettext('Weekly'), value: 'weekly' },
        { text: this.$gettext('Monthly'), value: 'monthly' },
      ],
      daily_options: [
        { text: this.$gettext('Mon'), value: "0" },
        { text: this.$gettext('Tue'), value: "1" },
        { text: this.$gettext('Wed'), value: "2" },
        { text: this.$gettext('Thu'), value: "3" },
        { text: this.$gettext('Fri'), value: "4" },
        { text: this.$gettext('Sat'), value: "5" },
        { text: this.$gettext('Sun'), value: "6" }
      ],
    }
  },
  computed: {
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
      storeRecurrent: "invoicesRecurrent/getRecurrent",
      invoice: "invoices/getInvoice",
      defaultRecurrent: "invoicesRecurrent/getDefaultRecurrent",
    }),
    isEdit () {
      return this.storeRecurrent != null
    },
    frequencyLabelText () {
      let labelText = ''
      if (this.recurrent.interval === 'daily') {
        labelText = this.$gettext('on')
      } else {
        labelText = this.$gettext('every')
      }
      return labelText
    },
    summaryLabelText () {
      let labelText = ''
      if (!this.isEdit) {
        labelText = this.$gettext('Summary')
      } else {
        labelText = this.$gettext('Next Delivery')
      }

      return labelText
    },
    nextDeliveryDate () {
      let next_date
      if (this.recurrent.next_date) {
        next_date = moment(new Date(this.recurrent.next_date)).locale(this.locale).format('dddd, MMMM Do, YYYY')
      } else {
        if (this.recurrent.start_date === Date.today().format('Y-m-d')) {
          next_date = this.$gettext('right now')
        } else {
          next_date = moment(new Date(this.recurrent.start_date)).locale(this.locale).format('dddd, MMMM Do, YYYY')
        }
      }
      return next_date
    },
    createSummary () {
      if (this.recurrent.interval === 'daily') {
        const matching_days = this.daily_options.filter(option => this.recurrent.days.includes(option.value)).map(option => option.text)
        var key1 = '', interval_value = ''
        if (matching_days.length == 7) {
          key1 = 'F11'
        } else {
          if (matching_days.length == 1) {
            key1 = 'F12'
            interval_value = matching_days
          } else {
            key1 = 'F12';
            var lastDay = matching_days.pop(matching_days.length-1);
            interval_value = matching_days.join(', ') + ' ' + this.$gettext("and") + ' ' + lastDay;
          }
        }
      } else if (this.recurrent.interval === 'weekly') {
        if (this.recurrent.skip == 1) {
          key1 = 'F21'
        } else {
          key1 = 'F22'
          interval_value = this.recurrent.skip
        }
      } else if (this.recurrent.interval === 'monthly') {
        if (this.recurrent.skip == 1) {
          key1 = 'F31'
        } else {
          key1 = 'F32'
          interval_value = this.recurrent.skip
        }
      }

      var key2='E1', end_value = '';
      if (this.recurrent.end_method === 'iteration') {
        if (this.recurrent.iterations == 1) {
          key2 = 'E2'
        } else {
          key2 = 'E3'
          end_value = this.recurrent.iterations
        }
      } else if (this.recurrent.end_method == 'date') {
        key2 = 'E4'
        end_value = this.recurrent.end_date
      }

      let sentence = this.$gettext("Send invoices every %(frequency)s %(end_method)s starting") + " " + this.recurrent.next_delivery_message
      if (key2 == 'E2'){
        sentence = this.$gettext("This invoice will be sent out only once") + " " + this.recurrent.next_delivery_message
      }

      var interval = {
        "F11": this.$gettext("day"),
        "F12": this.$gettext("%(interval)s"),
        "F21": this.$gettext("week"),
        "F22": this.$gettext("%(interval)s weeks"),
        "F31": this.$gettext("month"),
        "F32": this.$gettext("%(interval)s months")
      }

      var end = {
        "E1": "",
        "E2": this.$gettext("once"),
        "E3": this.$gettext("%(end)s times"),
        "E4": this.$gettext("until %(end)s")
      }
      const summary = sentence.replace("%(frequency)s", interval[key1].replace("%(interval)s", interval_value)).replace("%(end_method)s", end[key2].replace("%(end)s", end_value))
      this.$set(this.recurrent, 'summary', summary)
      return summary
    },
    recurrencySummary () {
      let sentence = this.$gettext('and afterwards, every %(freq) %(end_condition)')
      let freq = ''

      if (this.recurrent.interval === 'daily') {
        let days = this.$gettext('day') + ' '
        if (this.recurrent.days.length < 7) {
          const matching_days = this.daily_options.filter(option => this.recurrent.days.includes(option.value)).map(option => option.text)
          days = matching_days.join(', ')
        }
        freq = days
      } else if (this.recurrent.interval === 'weekly') {
        if (this.recurrent.skip === 1) {
          freq = this.$gettext("week")
        } else {
          freq = '%(interval) ' + this.$gettext("weeks")
        }
        freq = freq.replace('%(interval)', this.recurrent.skip)
      } else if (this.recurrent.interval === 'monthly') {
        if (this.recurrent.skip === 1) {
          freq = this.$gettext("month")
        } else {
          freq = '%(interval) ' + this.$gettext("months")
        }
        freq = freq.replace('%(interval)', this.recurrent.skip)
      }

      let end_condition = ''
      if (this.recurrent.end_method === 'iteration') {
        end_condition = this.$gettext('for %(iters) time(s)')
        end_condition = end_condition.replace('%(iters)', parseInt(this.recurrent.iterations) - 1)
      } else if (this.recurrent.end_method === 'date') {
        end_condition = this.$gettext('until %(end_date)')
        if (this.recurrent.end_date === 'string') {
          end_condition = end_condition.replace('%(end_date)', this.recurrent.end_date)
        } else {
          end_condition = end_condition.replace('%(end_date)', moment(new Date(this.recurrent.end_date)).format('DD-MM-YYYY'))
        }
      }

      sentence = sentence.replace('%(freq)', freq)
      return sentence.replace('%(end_condition)', end_condition)
    },
    locale () {
      return window.preferred_language;
    }
  },
  watch: {
    'recurrent.days' () {
      if (!this.isEdit) {
        this.setNextDeliveryMsg()
      }
    },
    'recurrent.end_method' (current_end_method) {
      switch (current_end_method) {
        case 'iteration':
          this.calculateEnddateFromIteration()
          break;
        case 'date':
          this.calculateIterationFromEnddate()
          break;
      }
    },
    'recurrent.iterations' (iterations) {
      this.calculateEnddateFromIteration()
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({
      toggleModal: 'invoicesRecurrent/TOGGLE_MODAL',
      setRecurrent: 'invoicesRecurrent/SET_RECURRENT',
      setSummary: 'invoicesRecurrent/SET_SUMMARY'
    }),
    ...mapActions({
      saveRecurrent: 'invoicesRecurrent/saveRecurrent'
    }),
    close () {
      document.body.classList.remove('modal-on')
      this.toggleModal()
      // this.setRecurrent(null)
    },
    setupData () {
      if (this.isEdit) {
        this.recurrent = this.storeRecurrent

        // set end_date as (today + 30 days) end_date if end_method !== 'date' 
        if (this.recurrent.end_method !== 'date') {
          let end_date = moment().add(1, 'months').format('YYYY-MM-DD')
          this.recurrent.end_date = end_date
        }
        // nullify iterations if end_method !== 'iteration' 
        if (this.recurrent.end_method !== 'iteration') {
          this.recurrent.iterations = 1
        }
        // reset days to default all checked if interval !== 'daily'
        if (this.recurrent.interval !== 'daily') {
          this.recurrent.days = this.daily_options.map(option => option.value)
        } 


      } else {

        // CREATE SETUP - DEFAULT DATA
        this.recurrent = this.defaultRecurrent

        this.$set(this.recurrent, 'start_date', this.invoice.invoice_date)
        this.$set(this.recurrent, 'iterations', 1)
        this.$set(this.recurrent, 'company_profile', this.currentCompany.id)

        this.setNextDeliveryMsg()
      }

      this.loading = false
    },
    onSubmit(event) {
      event.preventDefault()
    },
    openViewInvoice () {
      // this.close()
      // this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
       window.location.href = window.Urls['invoices_view_invoice'](this.recurrent.invoice.id)
    },
    checkValidation () {
      return this.$refs['recurrent-task-form'].reportValidity()
    },
    prepareAndUpdate () {
      if (this.checkValidation()) {
        const update_data = this.prepareDataForSubmit(this.recurrent)
        
        InvoiceRecurrentService.update(update_data.id, update_data).then((response) => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice Updated'),
            text: this.$gettext('Recurrent invoice updated successfully.'),
            variant: 'success',
            duration: 4
          })
          this.setSummary(this.summaryLabelText + ' ' + this.nextDeliveryDate + ' ' + this.recurrencySummary)

          this.setRecurrent(update_data)
          this.recurrent = this.storeRecurrent

          this.$emit('updated-invoice')
          this.close()
        })
      }
    },
    prepareAndSave () {
      if (this.checkValidation()) {
        this.saveRecurrent(this.prepareDataForSubmit(this.recurrent)).then((recurrentResponse) => {
          this.setSummary(this.summaryLabelText + ' ' + this.createSummary)

          this.setRecurrent(recurrentResponse)
          this.recurrent = this.storeRecurrent

          this.$emit('updated-invoice')
          this.close()
        })
      }
    },
    prepareDataForSubmit (recurrent) {
      const recurrent_copy = JSON.parse(JSON.stringify(recurrent));

      recurrent_copy.company_profile = this.currentCompany.id

      if (recurrent.invoice && recurrent.invoice.id) {
        recurrent_copy.invoice = recurrent.invoice.id
      } else if (this.invoice) {
        recurrent_copy.invoice = this.invoice.id
      }

      if (recurrent.interval === 'daily') {
        recurrent_copy.days = recurrent.days.join()
      } else {
        recurrent_copy.days = ''
      }

      let date_value
      date_value = Date.parse(recurrent_copy.start_date)
      if (date_value) {
        recurrent_copy.start_date = date_value.toString("yyyy-MM-dd")
      }
      date_value = Date.parse(recurrent_copy.end_date)
      if (date_value) {
        recurrent_copy.end_date = date_value.toString("yyyy-MM-dd")
      }
      return recurrent_copy
    },
    calculateEnddateFromIteration () {
      if (this.recurrent.start_date) {
        const new_date = new Date(this.recurrent.start_date)

        if (this.recurrent.interval === 'daily') {
          const days = this.recurrent.days

          let weeks = Math.floor(this.recurrent.iterations / days.length)
          if ((this.recurrent.iterations % days.length) === 0) weeks--

          const extraDays = this.recurrent.iterations - days.length * weeks

          let today_day = new Date(this.recurrent.start_date).getDay() - 1; // -1 is sunday
          if (today_day === -1) today_day = 6
          
          var c = 0, cnt = 0;
          for (var i = 0; i < 7; i++) {
            c++;
            if (days.indexOf(today_day.toString()) !== -1) {
              cnt++;
              if (cnt === extraDays) break
            }
            today_day++
            if (today_day === 7) today_day = 0
          }
          new_date.setDate(new_date.getDate() + (weeks) * 7 + c - 1)
        }
        if (this.recurrent.interval === 'weekly') {
          new_date.setDate(new_date.getDate() + this.recurrent.skip * (this.recurrent.iterations - 1) * 7)
        }
        if (this.recurrent.interval === 'monthly') {
          new_date.addMonths((this.recurrent.iterations - 1) * this.recurrent.skip)
        }
        this.recurrent.end_date = new_date.format('Y-m-d')
      }
    },
    calculateIterationFromEnddate () {
      var date_start_date = new Date(this.recurrent.start_date);
      var date_end_date = new Date(this.recurrent.end_date);
      if (this.recurrent.start_date) {
        if (date_start_date > date_end_date) {
          this.recurrent.end_date = this.recurrent.start_date;
          date_end_date = date_start_date;
        }
      }

      if (this.recurrent.interval === 'daily') {
        const weeksInBetween = Math.floor((date_end_date - date_start_date) / 1000 / 3600 / 24 + 1) / 7;
        const extraDays = Math.round((weeksInBetween - Math.trunc(weeksInBetween)) * 7);
        const nrOfWeeks = Math.trunc(weeksInBetween);
        const days = this.recurrent.days;
        this.recurrent.iterations = days.length * nrOfWeeks;

        var today_day = date_start_date.getDay() - 1; // -1 is sunday
        if (today_day === -1) today_day = 6

        var c= 0, cnt = 0;
        for (var i = 0; i < extraDays; i++) {
          if (days.indexOf(today_day.toString()) !== -1) {
            c = c + 1;
            cnt = cnt + 1;
            if (cnt === extraDays) break
          }
          today_day++;
          if (today_day === 7) today_day = 0
        }
        this.recurrent.iterations = this.recurrent.iterations + c;
      }
      if (this.recurrent.interval === 'weekly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 7 / this.recurrent.skip) + 1;
      }
      if (this.recurrent.interval === 'monthly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 30 / this.recurrent.skip) + 1;
      }
    },
    setNextDeliveryMsg () {
      let next_delivery_message = ''
      if (this.recurrent.interval === 'daily') {

        var today_day = new Date(this.recurrent.start_date).getDay() - 1; //-1 is sunday

        if (today_day == -1) {
          today_day = 6
        }
        var c = 0;
        for (var i = 0; i < 7; i++) {
          if (this.recurrent.days.indexOf(today_day.toString()) != -1) {
            break
          }
          today_day++
          if (today_day == 7) {
            today_day = 0
          }
          c++
        }

        var date_obj = new Date(this.recurrent.start_date)
        date_obj.setDate(date_obj.getDate() + c)

        this.$set(this.recurrent, 'start_date', date_obj.format('Y-m-d'))

        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          next_delivery_message = this.$gettext('right now')
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      } else {
        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          next_delivery_message = this.$gettext('right now')
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      }
      this.$set(this.recurrent, 'next_delivery_message', next_delivery_message)
    },

  }
}
</script>

<style lang="scss">
.gk-modal-recurrent-invoice {
  .gk-modal {
    max-width: 530px !important;
    .action-buttons-wrapper {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }
    .form-row { align-items: baseline; }
    .form-group label { font-size: 1rem; }
    #recurrent-input-group-comments {
      overflow-y: auto !important;
    }
    #recurrent-input-group-interval {
      .form-group { align-items: center; }
        #recurrent-input-frequency {
        .custom-control { width: 55px; }
      }
    }
    #recurrent-input-group-ends {
      margin-bottom: 2rem;
    }
    #recurrent-input-group-summary {
      font-size: 1rem;
      padding: 1rem 0;
      border: 1px solid $colorBrand;
      border-radius: $radiusLg;
    }
  }
}
</style>